import type { ProgressData } from '@dcs/contracts';
import type { Progress, ProgressListener, ProgressRepo } from '../ports/progress.ts';
import type { KeyValueStorage } from '../ports/storage.ts';
import {
  capProgressTimes,
  emptyProgressData,
  fromProgressData,
  marksOf,
  sameProgressData,
  toProgressData,
  trimProgress,
  unionProgress,
} from './merge.ts';
import { createStorageWriter } from './storage-writer.ts';
import {
  STASH_TTL_MS,
  marksKey,
  ownerKey,
  readKey,
  readMarks,
  readStash,
  removeKey,
  stashExpired,
  stashKey,
  writeKey,
  writeMarks,
  writeStash,
} from './sync-storage.ts';
import type { ProgressStash, SyncMarks } from './sync-storage.ts';
import { isUnauthorized } from './transport.ts';
import type { ProgressTransport, SyncTimers } from './transport.ts';

/** Minimum time between two PUT cycles of one license class (F6 AC-1.4). */
export const SYNC_INTERVAL_MS = 30_000;
/** Upload guard against a device clock running ahead: no time later than now + 5 min is sent. */
export const MAX_CLOCK_SKEW_MS = 5 * 60_000;

export type SyncPhase = 'sign-in' | 'push' | 'conflict';

/** `active`; `unauthorized`: a 401 stopped it until the next sign-in; `stopped`: disposed. */
export type SyncStatus = 'active' | 'unauthorized' | 'stopped';

/**
 * How a signed-in session ended:
 *  - `sign-out-explicit`: the user pressed "Đăng xuất". Nothing of them stays on the device (no
 *    stash, and their old stash is deleted). The caller flushes first (`flush`, 30 s spacing kept):
 *    changes younger than the spacing window that could not be sent are lost (< 30 s of answers).
 *  - `session-ended`: the session expired or was revoked elsewhere. The dirty classes are stashed
 *    for this user (7-day TTL) so their next sign-in on this device uploads them, merged into a
 *    stash this user already has (another tab whose session ended first). Nothing is stashed when
 *    the device no longer belongs to this user and holds no stash of theirs: another tab's explicit
 *    sign-out (or the account deletion) cleared the owner marker, and that must stay final.
 * Both remove the local records, their marks and the owner marker while the device is still this
 * user's (owner marker, or their stash left by another tab whose session ended first); otherwise the
 * records are no longer theirs (guest data written since, or another account that claimed the
 * device) and are left alone.
 */
export type SyncEndReason = 'sign-out-explicit' | 'session-ended';

export interface SyncingProgressRepoOptions {
  /** The local repo: the source of truth the UI reads and writes, never waiting for the network. */
  local: ProgressRepo;
  /**
   * The storage `local` reads. Holds the owner marker, the per-class sync marks (tombstones, last
   * PUT) and the session-expiry stash (see `sync-storage.ts`).
   */
  storage: KeyValueStorage;
  /** Key prefix, same as the local repo's. Default `dcs`. */
  namespace?: string;
  /** The signed-in user this repo syncs for. */
  userId: string;
  /** Replaces a whole local record after a merge. Default `createStorageWriter(storage)`. */
  writeLocal?: (progress: Progress) => void;
  transport: ProgressTransport;
  /** Epoch ms. Injected: this module never reads the clock. */
  now: () => number;
  timers: SyncTimers;
  /** Minimum time between PUT cycles of one class. Default 30 s. */
  intervalMs?: number;
  /** How long a session-expiry stash is kept. Default 7 days. */
  stashTtlMs?: number;
  /**
   * Classes the app writes. Merged at sign-in besides the server's ones, and the set that is
   * discarded (another user's data) or cleared (sign-out) locally: list every class the app uses.
   */
  licenseClasses?: readonly string[];
  /**
   * Subscribes `flush` to the page going away (`browserPageLifecycle`); returns the unsubscribe. The
   * flush sends, with `keepalive`, the dirty classes whose last PUT is at least 30 s old; the others
   * stay dirty (they are stored locally and go out with the next PUT or the next session).
   */
  onPageHide?: (flush: () => void) => () => void;
  /** Failures are retried by the repo (a 401 is not); this is for logging only. */
  onError?: (error: unknown, context: { licenseClass: string | null; phase: SyncPhase }) => void;
}

export interface SyncingProgressRepo extends ProgressRepo {
  /**
   * Sign-in flow: reads all progress from the server, merges it with the local one per class,
   * writes the merge locally and PUTs the classes whose merge differs from the server (one PUT per
   * class, 30 s spacing respected). If the server cannot be reached, local classes are marked dirty.
   */
  syncAll(): Promise<void>;
  /**
   * Sends the dirty classes whose 30 s spacing allows a PUT now (with `keepalive`); the others keep
   * their timer. Resolves when those and the PUT cycles already running settled (never rejects).
   */
  flush(): Promise<void>;
  /** Classes with changes not yet on the server. */
  pending(): readonly string[];
  status(): SyncStatus;
  /**
   * The page or the component goes away: stops timers and listeners and aborts requests in flight.
   * Local data and its owner are untouched (the next sign-in of the same user merges them).
   */
  dispose(): void;
  /**
   * The session ended (`SyncEndReason`). `session-ended` stashes the dirty classes for this user
   * (merged into their existing stash; nothing when another tab's explicit sign-out already took
   * the device); `sign-out-explicit` writes no stash and deletes this user's. Then, if the owner
   * marker still names this user, the local progress of every known class, its sync marks and the
   * owner marker are removed (the browser shows a guest nothing of the account), and `dispose`.
   */
  signOut(reason: SyncEndReason): void;
}

/** `onSignIn(repo)`: the flow to run when the session becomes signed-in. */
export function onSignIn(repo: Pick<SyncingProgressRepo, 'syncAll'>): Promise<void> {
  return repo.syncAll();
}

interface ClassState {
  dirty: boolean;
  /** When the oldest unsent change was made. */
  dirtySince: number;
  /** When the last PUT cycle started (successful or not), also from a previous page. */
  lastAttemptAt: number | undefined;
  /** `updatedAt` of the server row our data is based on; `undefined` = no row seen. */
  base: string | undefined;
  timer: unknown;
  hasTimer: boolean;
  /** The server holds data this version cannot read: do not touch it. */
  blocked: boolean;
  /** A PUT cycle is running (its outcome is unknown until it settles). */
  inFlight: boolean;
  chain: Promise<void>;
}

function isUsable(data: ProgressData): boolean {
  const value = data as { v?: unknown; done?: unknown; wrong?: unknown; updatedAt?: unknown };
  return (
    value.v === 1 &&
    typeof value.done === 'object' &&
    value.done !== null &&
    Array.isArray(value.wrong) &&
    typeof value.updatedAt === 'number'
  );
}

function hasContent(data: ProgressData): boolean {
  return (
    data.updatedAt > 0 ||
    data.wrong.length > 0 ||
    Object.keys(data.done).length > 0 ||
    data.resetAt !== undefined ||
    data.wrongClearedAt !== undefined ||
    Object.keys(data.bookmarks ?? {}).length > 0
  );
}

/**
 * `ProgressRepo` that reads and writes the local repo at once and syncs with the server in the
 * background for one signed-in user:
 *
 *  - Spacing (AC-1.4): writes mark a class dirty; a PUT cycle of a class starts at most once every
 *    30 s (the time of the last one is stored, so a reload does not reset it), and carries all the
 *    changes (50 answers in half a minute = 1 PUT). A hidden page flushes only the classes whose
 *    last PUT is 30 s old or more. Within one cycle a 409 (nothing written) reads the server,
 *    merges and PUTs once more; a second 409 waits for the next tick. A network failure keeps the
 *    class dirty for the next tick; a 401 stops the sync until the next sign-in.
 *  - Ownership (privacy): local records carry an owner (`<ns>:progress-owner`). Created for user U:
 *    no owner (guest data) → the data becomes U's and is merged and uploaded (AC-2.3); owner U →
 *    merged as usual; another owner → the local records are discarded, never uploaded, and U's
 *    server data is adopted, and another user's session-expiry stash is deleted. `signOut` clears
 *    the local records, so the next user of the browser starts from nothing; only a session that
 *    ended by itself leaves a stash of the unsent classes for U (7 days, see `SyncEndReason`).
 *  - Tombstones: `reset` and `clearWrong` record `resetAt` / `wrongClearedAt` (sync marks), sent
 *    with the data, so the next merge on any device does not bring the old answers back. A removed
 *    bookmark stays in the local record as `on: false` (F8 T-3.5) for the same reason.
 *  - Clock skew: uploaded times are capped at now + 5 min.
 */
export function createSyncingProgressRepo(
  options: SyncingProgressRepoOptions,
): SyncingProgressRepo {
  const { local, transport, timers, storage, userId } = options;
  const namespace = options.namespace ?? 'dcs';
  const writeLocal = options.writeLocal ?? createStorageWriter(storage, namespace);
  const interval = options.intervalMs ?? SYNC_INTERVAL_MS;
  const stashTtl = options.stashTtlMs ?? STASH_TTL_MS;
  const states = new Map<string, ClassState>();
  const knownClasses = new Set<string>(options.licenseClasses ?? []);
  const listeners = new Set<ProgressListener>();
  let disposed = false;
  let unauthorized = false;
  let signedOut = false;

  const active = (): boolean => !disposed && !unauthorized;

  const report = (error: unknown, licenseClass: string | null, phase: SyncPhase): void => {
    try {
      options.onError?.(error, { licenseClass, phase });
    } catch {
      // A broken logger must not break the sync.
    }
  };

  const notify: ProgressListener = (licenseClass, progress) => {
    for (const listener of [...listeners]) listener(licenseClass, progress);
  };
  const stopForwarding = local.subscribe(notify);

  const marksOfClass = (licenseClass: string): SyncMarks =>
    readMarks(storage, licenseClass, namespace);
  const updateMarks = (licenseClass: string, patch: SyncMarks): void => {
    writeMarks(storage, licenseClass, namespace, { ...marksOfClass(licenseClass), ...patch });
  };

  /** Local record + tombstones, contract shape. */
  const localData = (licenseClass: string): ProgressData =>
    toProgressData(local.load(licenseClass), marksOfClass(licenseClass));

  /** What goes over the wire: times capped (clock skew), then within the 16 KB contract. */
  const toUpload = (data: ProgressData): ProgressData =>
    trimProgress(capProgressTimes(data, options.now() + MAX_CLOCK_SKEW_MS));

  function stateOf(licenseClass: string): ClassState {
    let state = states.get(licenseClass);
    if (!state) {
      const lastPutAt = marksOfClass(licenseClass).lastPutAt;
      state = {
        dirty: false,
        dirtySince: 0,
        // A stored time ahead of the clock (clock moved back) counts as "just now".
        lastAttemptAt: lastPutAt === undefined ? undefined : Math.min(lastPutAt, options.now()),
        base: undefined,
        timer: undefined,
        hasTimer: false,
        blocked: false,
        inFlight: false,
        chain: Promise.resolve(),
      };
      states.set(licenseClass, state);
      knownClasses.add(licenseClass);
    }
    return state;
  }

  function cancelTimer(state: ClassState): void {
    if (state.hasTimer) timers.clearTimeout(state.timer);
    state.hasTimer = false;
    state.timer = undefined;
  }

  const spacingAllows = (state: ClassState): boolean =>
    state.lastAttemptAt === undefined || options.now() - state.lastAttemptAt >= interval;

  /** Arms the timer: at least `interval` after the later of "first unsent change" and "last PUT". */
  function schedule(licenseClass: string, state: ClassState): void {
    if (!active() || !state.dirty || state.blocked || state.hasTimer) return;
    const from = Math.max(state.dirtySince, state.lastAttemptAt ?? state.dirtySince);
    const delay = Math.max(0, from + interval - options.now());
    state.hasTimer = true;
    state.timer = timers.setTimeout(() => {
      state.hasTimer = false;
      state.timer = undefined;
      void syncNow(licenseClass, false);
    }, delay);
  }

  function markDirty(licenseClass: string): void {
    const state = stateOf(licenseClass);
    if (!state.dirty) {
      state.dirty = true;
      state.dirtySince = options.now();
    }
    schedule(licenseClass, state);
  }

  /** Writes `data` as the local record (+ tombstones) of the class and tells the screens. */
  function replaceLocal(licenseClass: string, data: ProgressData, prev: Progress): void {
    const next = fromProgressData(licenseClass, data, prev);
    writeLocal(next);
    updateMarks(licenseClass, marksOf(data));
    notify(licenseClass, next);
  }

  /** Merges `server` into the local record (written locally, untrimmed); returns what to upload. */
  function mergeIntoLocal(licenseClass: string, server: ProgressData): ProgressData {
    const prev = local.load(licenseClass);
    const mine = toProgressData(prev, marksOfClass(licenseClass));
    const union = unionProgress(mine, server);
    if (!sameProgressData(mine, union)) replaceLocal(licenseClass, union, prev);
    return toUpload(union);
  }

  /** Drops the local record of a class and all its sync marks (tombstones, last PUT time). */
  function discardLocal(licenseClass: string): void {
    local.reset(licenseClass);
    removeKey(storage, marksKey(licenseClass, namespace));
  }

  /** Brings back what this user had not uploaded when their session ended on this browser. */
  function restoreStash(stash: ProgressStash): void {
    for (const [licenseClass, entry] of Object.entries(stash.classes)) {
      knownClasses.add(licenseClass);
      const prev = local.load(licenseClass);
      const mine = toProgressData(prev, marksOfClass(licenseClass));
      const union = unionProgress(mine, entry.data);
      if (!sameProgressData(mine, union)) replaceLocal(licenseClass, union, prev);
      if (entry.lastPutAt !== undefined && marksOfClass(licenseClass).lastPutAt === undefined) {
        updateMarks(licenseClass, { lastPutAt: entry.lastPutAt });
      }
      markDirty(licenseClass);
    }
  }

  /**
   * Who the local records belong to, decided before anything can be uploaded. The device's stash
   * is read once: expired (7 days) or another user's → deleted; this user's → merged back in.
   */
  function claimLocalData(): void {
    const owner = readKey(storage, ownerKey(namespace));
    if (owner !== null && owner !== userId) {
      // Another account's progress (it did not sign out here): never upload it, never show it.
      for (const licenseClass of knownClasses) discardLocal(licenseClass);
    }
    writeKey(storage, ownerKey(namespace), userId);
    const stash = readStash(storage, namespace);
    if (stash === null) return;
    removeKey(storage, stashKey(namespace));
    if (stash.userId === userId && !stashExpired(stash, options.now(), stashTtl)) {
      restoreStash(stash);
    }
  }

  /** This user's stash on the device, if any and not expired. */
  function ownStash(): ProgressStash | null {
    const existing = readStash(storage, namespace);
    return existing !== null &&
      existing.userId === userId &&
      !stashExpired(existing, options.now(), stashTtl)
      ? existing
      : null;
  }

  /**
   * Stashes the dirty classes of this user for their next sign-in (session ended by itself), merged
   * into `mine`, the stash another tab of the same user wrote when its session ended first.
   */
  function stashUnsent(mine: ProgressStash | null): void {
    const classes: ProgressStash['classes'] = { ...mine?.classes };
    let added = false;
    for (const licenseClass of knownClasses) {
      const state = states.get(licenseClass);
      if (!state || !(state.dirty || state.inFlight)) continue;
      const data = localData(licenseClass);
      if (!hasContent(data)) continue;
      const prior = classes[licenseClass];
      const merged = prior ? unionProgress(prior.data, data) : data;
      const times = [prior?.lastPutAt, state.lastAttemptAt].filter(
        (t): t is number => t !== undefined,
      );
      classes[licenseClass] =
        times.length === 0 ? { data: merged } : { data: merged, lastPutAt: Math.max(...times) };
      added = true;
    }
    if (!added) return;
    writeStash(storage, namespace, { userId, savedAt: options.now(), classes });
  }

  function stopForUnauthorized(error: unknown, licenseClass: string | null, phase: SyncPhase) {
    unauthorized = true;
    for (const state of states.values()) cancelTimer(state);
    report(error, licenseClass, phase);
  }

  /**
   * One PUT cycle for a class: PUT; on 409 read the server, merge, PUT once more. Never starts
   * within `interval` of the previous cycle. Never rejects.
   */
  async function push(licenseClass: string, keepalive: boolean): Promise<void> {
    const state = stateOf(licenseClass);
    if (!active() || !state.dirty || state.blocked) return;
    if (!spacingAllows(state)) {
      schedule(licenseClass, state);
      return;
    }
    cancelTimer(state);
    state.dirty = false;
    state.inFlight = true;
    state.lastAttemptAt = options.now();
    updateMarks(licenseClass, { lastPutAt: state.lastAttemptAt });
    let phase: SyncPhase = 'push';
    try {
      let data = toUpload(localData(licenseClass));
      for (let attempt = 0; attempt < 2; attempt += 1) {
        const body = state.base === undefined ? { data } : { data, baseUpdatedAt: state.base };
        const result = await transport.put(licenseClass, body, { keepalive });
        if (result.status === 'saved') {
          state.base = result.progress.updatedAt;
          return;
        }
        if (attempt === 1) {
          // Raced again: leave it for the next tick instead of looping.
          state.dirty = true;
          return;
        }
        phase = 'conflict';
        const server = await transport.get(licenseClass);
        if (!active()) return;
        if (server && !isUsable(server.data)) {
          state.blocked = true;
          return;
        }
        data = server ? mergeIntoLocal(licenseClass, server.data) : data;
        state.base = server?.updatedAt;
      }
    } catch (error) {
      state.dirty = true;
      if (isUnauthorized(error)) stopForUnauthorized(error, licenseClass, phase);
      else if (active()) report(error, licenseClass, phase);
    } finally {
      state.inFlight = false;
      schedule(licenseClass, state);
    }
  }

  function syncNow(licenseClass: string, keepalive: boolean): Promise<void> {
    const state = stateOf(licenseClass);
    const run = state.chain.then(() => push(licenseClass, keepalive));
    state.chain = run;
    return run;
  }

  async function flush(): Promise<void> {
    const runs: Promise<void>[] = [];
    for (const [licenseClass, state] of states) {
      runs.push(state.dirty && spacingAllows(state) ? syncNow(licenseClass, true) : state.chain);
    }
    await Promise.all(runs);
  }

  async function syncAll(): Promise<void> {
    if (!active()) return;
    let rows: Awaited<ReturnType<ProgressTransport['list']>>;
    try {
      rows = await transport.list();
    } catch (error) {
      if (isUnauthorized(error)) {
        stopForUnauthorized(error, null, 'sign-in');
        return;
      }
      if (!active()) return;
      report(error, null, 'sign-in');
      // Cannot compare with the server: the normal push (409 → merge) will do it later.
      for (const licenseClass of knownClasses) {
        if (hasContent(localData(licenseClass))) markDirty(licenseClass);
      }
      return;
    }
    if (!active()) return;
    const byClass = new Map(rows.map((row) => [row.licenseClass, row]));
    const pushes: Promise<void>[] = [];
    for (const licenseClass of new Set([...knownClasses, ...byClass.keys()])) {
      const state = stateOf(licenseClass);
      const row = byClass.get(licenseClass);
      if (row && !isUsable(row.data)) {
        state.blocked = true;
        continue;
      }
      state.blocked = false;
      state.base = row?.updatedAt;
      const upload = mergeIntoLocal(licenseClass, row?.data ?? emptyProgressData());
      const needed = row ? !sameProgressData(upload, row.data) : hasContent(upload);
      if (!needed) {
        state.dirty = false;
        cancelTimer(state);
        continue;
      }
      markDirty(licenseClass);
      // Within 30 s of the previous PUT, `push` leaves it to the timer armed by `markDirty`.
      pushes.push(syncNow(licenseClass, false));
    }
    await Promise.all(pushes);
  }

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    for (const state of states.values()) cancelTimer(state);
    stopForwarding();
    stopLifecycle?.();
    listeners.clear();
    try {
      transport.abort?.();
    } catch {
      // Nothing left to cancel.
    }
  }

  claimLocalData();

  const stopLifecycle = options.onPageHide?.(() => {
    void flush();
  });

  return {
    load: (licenseClass) => local.load(licenseClass),
    recordAnswer(licenseClass, questionId, choice, correct, now) {
      const saved = local.recordAnswer(licenseClass, questionId, choice, correct, now);
      markDirty(licenseClass);
      return saved;
    },
    setBookmark(licenseClass, questionId, on, now) {
      const saved = local.setBookmark(licenseClass, questionId, on, now);
      markDirty(licenseClass);
      return saved;
    },
    clearWrong(licenseClass, now) {
      const saved = local.clearWrong(licenseClass, now);
      const previous = marksOfClass(licenseClass).wrongClearedAt;
      updateMarks(licenseClass, { wrongClearedAt: Math.max(previous ?? 0, now) });
      markDirty(licenseClass);
      return saved;
    },
    reset(licenseClass) {
      const saved = local.reset(licenseClass);
      const previous = marksOfClass(licenseClass).resetAt;
      updateMarks(licenseClass, { resetAt: Math.max(previous ?? 0, options.now()) });
      markDirty(licenseClass);
      return saved;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    syncAll,
    flush,
    pending: () =>
      [...states].filter(([, state]) => state.dirty).map(([licenseClass]) => licenseClass),
    status: () => (disposed ? 'stopped' : unauthorized ? 'unauthorized' : 'active'),
    dispose,
    signOut(reason) {
      if (signedOut) return;
      signedOut = true;
      // Still this user's device: the owner marker names them, or (session ended) it was removed by
      // another tab of theirs whose session ended first and left their stash. Otherwise another
      // tab's explicit sign-out or the account deletion took it (T-2.4 F1): nothing is stashed,
      // and the records, now a guest's or another account's, are left alone.
      const owned = readKey(storage, ownerKey(namespace)) === userId;
      const mine = reason === 'session-ended' ? ownStash() : null;
      if (reason === 'session-ended') {
        if (owned || mine !== null) stashUnsent(mine);
      } else if (readStash(storage, namespace)?.userId === userId) {
        removeKey(storage, stashKey(namespace));
      }
      if (owned || mine !== null) {
        // Screens are told (the listeners are still attached), then the sync stops.
        for (const licenseClass of knownClasses) discardLocal(licenseClass);
        removeKey(storage, ownerKey(namespace));
      }
      dispose();
    },
  };
}
